<script setup lang="ts">
import { faArrowsRetweet, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { type InferType, object, string } from "yup";
import type { Automation } from "~/models/Automation";
import type { Form as UiForm } from "#ui/types/form";
import type { ApiResponse } from "~/types/ApiResponse";
import { faBolt, faClock } from "@fortawesome/pro-solid-svg-icons";
import type { Template } from "~/models/Template";

const api = useApi();

const { created = undefined } = defineProps<{
    created?: (automation: Automation) => void | Promise<void>;
    label?: string;
    app?: string;
    tags?: string[];
}>();

const types = [
    {
        icon: faBolt,
        label: "Event-Based",
        value: "trigger",
        description: "E.g. when a candidate is placed",
    },
    {
        icon: faClock,
        label: "Time-Based",
        value: "scheduled",
        description: "E.g. weekly or tomorrow morning at 9AM",
    },
];
const open = defineModel<boolean>("open", { default: false });
const schema = object({
    name: string()
        .min(
            2,
            "Enter a descriptive name for your automation - the name must be at least 2 characters.",
        )
        .max(255)
        .required(),
    type: string().oneOf(types.map(({ value }) => value)),
});
type Schema = InferType<typeof schema>;

const state = ref<{
    name: Automation["name"];
    type: Automation["type"];
    scheduled_config: Automation["scheduled_config"];
}>({
    name: "",
    type: "trigger",
    scheduled_config: undefined,
});

const form = ref<UiForm<Schema>>();

const { onSubmit, submitting } = useFormSubmission<
    Schema,
    ApiResponse<Automation>
>(
    form,
    async (event) => {
        return api.automations.create(event.data);
    },
    {
        onSuccess: (response: ApiResponse<Automation>) => {
            if (created) created(response.data as Automation);
            else navigateTo(`/automations/${response.data.uuid}/edit`);
        },
        onError: (error) => {},
    },
);

const using = ref<boolean>(false);

async function select(template: Template) {
    if (using.value) return;
    using.value = true;

    try {
        if (template.slug === "blank-automation") {
            state.value.name = "Blank Automation";
            await form.value?.submit();
        } else {
            await api.templates.use(template);
        }
    } catch (e) {
    } finally {
        using.value = false;
    }
}

const { user } = useUser();
</script>

<template>
    <USlideover
        v-model="open"
        :ui="{
            width: 'w-screen max-w-screen-2xl',
        }"
    >
        <UForm
            ref="form"
            :schema="schema"
            :state="state"
            class="h-full"
            @submit="onSubmit"
            :validate-on="['submit']"
        >
            <UCard
                class="flex flex-col flex-1 h-full min-h-[0] overflow-hidden"
                :ui="{
                    background: 'bg-white',
                    rounded: '',
                    body: {
                        base: 'flex-1 min-h-[0] overflow-y-auto',
                        padding: '',
                    },
                    ring: '',
                    divide: 'divide-y divide-gray-300',
                }"
            >
                <template #header>
                    <!-- Content -->
                    <div class="flex items-center gap-4">
                        <h3
                            class="text-base font-medium font-display leading-6 text-gray-900 dark:text-white"
                        >
                            <FontAwesomeIcon
                                :icon="faArrowsRetweet"
                                class="mr-2"
                            />
                            {{ label ?? "New Automation" }}
                        </h3>
                        <div class="flex-1"></div>
                        <UButton
                            square
                            color="gray"
                            variant="ghost"
                            size="sm"
                            @click="open = false"
                        >
                            <FontAwesomeIcon :icon="faTimes" size="lg" />
                        </UButton>
                    </div>
                </template>

                <!-- Content -->
                <div
                    class="h-full space-y-6 py-6 sm:space-y-0 sm:divide-y overflow-y-auto min-h-[0] sm:divide-gray-200 sm:py-0"
                >
                    <AppTemplateLibrary
                        type="automation"
                        :app="app"
                        :tags="tags"
                        :templates="[
                            api.templates.createEmpty('automation', {
                                title: 'Blank Automation',
                                slug: 'blank-automation',
                                description:
                                    'Start from scratch and build your automation exactly how you want it. Define the starting point and tasks to create a fully tailored automation to your specific needs.',
                                is_featured: true,
                                is_blank: true,
                                app: {
                                    label: 'HireData',
                                    icon: useAppLogoUrl({ type: 'icon' }),
                                    logo: useAppLogoUrl(),
                                    value: 'hiredata',
                                    ...(user?.account?.core_app ?? {}),
                                },
                            }),
                        ]"
                        @select="select"
                        class="p-4"
                    />
                </div>
            </UCard>
        </UForm>
    </USlideover>
</template>

<style scoped></style>
